import React from "react";
import { GetElsLookupsState } from "../redux/reducers/getLookups/getElsLookupsReducer";
import { Option } from "../components/common/Dropdown/Dropdown";
import i18n from "../i18n";
import IncomeUnitType from "../types/unit/IncomeUnit";
import { ColumnInfo } from "./DataTable/ColumnInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";

export const getInternalFilteringColumns = (elsLookupsState: GetElsLookupsState, data: IncomeUnitType[]) => {
    return [{
        id: "buildingIdNumber",
        type: "integer",
        display: i18n.t("internal-headings.sequence-number", { ns: "lease_table" })
    },
    {
        id: "tenancyTypeCode",
        display: i18n.t("internal-headings.tenancy-type", { ns: "lease_table" }),
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["tenancyTypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.tenancyTypeCode)).includes(opt.value as string))
    },
    {
        id: "designTypeCode",
        display: i18n.t("internal-headings.design-type", { ns: "lease_table" }),
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["designTypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.designTypeCode)).includes(opt.value as string))
    }]
}

export const getColumns = (elsLookupsState: GetElsLookupsState, data: IncomeUnitType[], subTypeCodeOptions: Option[], leaseTypeCodeOptions: Option[], leaseStatusCodeOptions: Option[], comparator: (a: number, b: number) => number, headerActions: JSX.Element[]) => {
    return [{
        type: "boolean",
        filterDisabled: true,
        sortingId: "tenantId",
        comparator: comparator,
        noSortingDisplay: <FontAwesomeIcon icon={faSort} />,
    }, {
        id: "tenantUnitNumber",
        display: i18n.t('headings.unit-number', { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.unit-number-tooltip', { ns: "lease_table" }),
    },
    {
        id: "tenantName",
        display: i18n.t("internal-headings.tenant-name", { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.tenant-name-tooltip', { ns: "lease_table" }),
    },
    {
        id: "unitSubtypeCode",
        display: i18n.t('headings.unit-use', { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.unit-use-tooltip', { ns: "lease_table" }),
        dataLkpFunc: (val: unknown) => subTypeCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
        filterDisplay: i18n.t('headings.unit-use-internal', { ns: "lease_table" }),
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["unitSubtypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.unitSubtypeCode)).includes(opt.value as string))
    },
    {
        id: "startDate",
        display: i18n.t('internal-headings.start-date', { ns: "lease_table" }),
        type: "date",
        tooltip: i18n.t('tooltips.start-date-tooltip', { ns: "lease_table" }),
    },
    {
        id: "netLeasableArea",
        display: i18n.t('internal-headings.leased-area', { ns: "lease_table" }),
        type: "number",
        tooltip: i18n.t('tooltips.leased-area-tooltip', { ns: "lease_table" }),
    },
    {
        id: "currentBaseRent",
        display: i18n.t('internal-headings.current-base-rent', { ns: "lease_table" }),
        type: "number",
        tooltip: i18n.t('tooltips.current-base-rent-tooltip', { ns: "lease_table" }),
    },
    {
        id: "leaseTypeCode",
        display: i18n.t('internal-headings.lease-type', { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.lease-type-tooltip', { ns: "lease_table" }),
        dataLkpFunc: (val: unknown) => leaseTypeCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["leaseTypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.leaseTypeCode)).includes(opt.value as string))
    },
    {
        id: "leaseStatusCode",
        display: i18n.t('internal-headings.lease-status', { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.lease-status-tooltip', { ns: "lease_table" }),
        dataLkpFunc: (val: unknown) => leaseStatusCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["leaseStatusCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.leaseStatusCode)).includes(opt.value as string))
    },
    {
        id: "analysisFlag",
        display: i18n.t("internal-headings.analysis-flag", { ns: "lease_table" }),
        tooltip: i18n.t('tooltips.analysis-flag-tooltip', { ns: "lease_table" }),
        filterDropdown: (([{ text: "Blank (Null)", value: "null" }] as Option[]).concat(elsLookupsState.dropdowns["rentAnalysisOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.analysisFlag)).includes(opt.value as string))
    }, {
        filterDisabled: true,
        sortingDisabled: true,
        headerActions: headerActions
    }] as ColumnInfo[]
}

export const getCSVColumns = (elsLookupsState: GetElsLookupsState) => {
    return [{
        id: "variables",
        display: i18n.t("internal-headings.variables", { ns: "lease_table" }),
        filterMultiDropdown: (elsLookupsState.dropdowns["variablesOptions"] as Option[]).filter(opt => opt && opt.value !== "")
    }, {
        id: "floorLevel",
        display: i18n.t('headings.floor-level', { ns: "unit" }),
        filterDropdown: (([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["floorLevelOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
    }, {
        id: "includeInsuranceFlag",
        display: i18n.t("headings.insurance", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includeManagementFlag",
        display: i18n.t("headings.management", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includeMaintenanceFlag",
        display: i18n.t("headings.maintenance", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includeHydroFlag",
        display: i18n.t("headings.hydro", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includeWaterFlag",
        display: i18n.t("headings.water", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includeHvacFlag",
        display: i18n.t("headings.hvac", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "includePropertyTaxFlag",
        display: i18n.t("headings.property-tax", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "landLeaseFlag",
        display: i18n.t("headings.land-lease", { ns: "unit" }),
        type: "boolean"
    }, {
        id: "annualRent",
        display: i18n.t('headings.annual-rent', { ns: "unit" }),
        type: "number"
    }, {
        id: "propertyTaxRecovery",
        display: i18n.t('headings.prop-tax-recoveries', { ns: "unit" }),
        type: "number"
    }, {
        id: "operatingRecovery",
        display: i18n.t('headings.operating-recoveries', { ns: "unit" }),
        type: "number"
    }, {
        id: "tenantImprovement",
        display: i18n.t('headings.tenant-improvements', { ns: "unit" }),
        type: "number"
    }, {
        id: "freeRentMonth",
        display: i18n.t('headings.rent-free-months', { ns: "unit" }),
        type: "number"
    }, {
        id: "negotiatedDate",
        display: i18n.t('headings.negotiated-date', { ns: "unit" }),
        type: "date"
    }, {
        id: "endDate",
        display: i18n.t('headings.end-date', { ns: "unit" }),
        type: "date"
    }, {
        id: "stepUpDate1",
        display: i18n.t('headings.step-date1-placeholder', { ns: "unit" }),
        type: "date"
    }, {
        id: "stepUpRate1",
        display: i18n.t('headings.step-rent1', { ns: "unit" }),
        type: "number"
    }, {
        id: "stepUpDate2",
        display: i18n.t('headings.step-date2-placeholder', { ns: "unit" }),
        type: "date"
    }, {
        id: "stepUpRate2",
        display: i18n.t('headings.step-rent2', { ns: "unit" }),
        type: "number"
    }, {
        id: "stepUpDate3",
        display: i18n.t('headings.step-date3-placeholder', { ns: "unit" }),
        type: "date"
    }, {
        id: "stepUpRate3",
        display: i18n.t('headings.step-rent3', { ns: "unit" }),
        type: "number"
    }, {
        id: "stepUpDate4",
        display: i18n.t('headings.step-date4-placeholder', { ns: "unit" }),
        type: "date"
    }, {
        id: "stepUpRate4",
        display: i18n.t('headings.step-rent4', { ns: "unit" }),
        type: "number"
    }]
}

